import {
  IStatementActivityLookback,
  IStatementBuilderActivityLookbackResponse,
  IStatementBuilderCreateTemplateRequest,
  IStatementBuilderEnabledRequest,
  IStatementBuilderEnabledResponse,
  IStatementBuilderMessageRequest,
  IStatementBuilderMessageResponse,
  IStatementBuilderOptionsResponse,
  IStatementBuilderPreviewResponse,
  IStatementBuilderPublishResponse,
  IStatementBuilderRenameTemplateRequest,
  IStatementBuilderResetResponse,
  IStatementBuilderRevertResponse,
  IStatementBuilderState,
  IStatementBuilderTemplateResponse,
  IStatementBuilderTemplatesResponse,
  IStatementTemplateOptions,
} from 'shared';

import { orgHeader } from '../../../../common/lib/axiosBaseQuery';
import { CacheTags } from '../../types/cacheTags';
import { emptySplitApi } from '../baseApi';

type StatementBuilderOrgArgs = {
  organisationId: string;
};

type StatementBuilderQueryArgs = StatementBuilderOrgArgs & {
  templateId: number;
};

type StatementBuilderPreviewArgs = StatementBuilderQueryArgs & {
  /** Preview with this contact's real outstanding statement instead of sample data */
  contactId?: number;
};

const LIST_TAG = { type: CacheTags.StatementBuilder, id: `LIST` } as const;
const templateTag = (templateId: number) => ({ type: CacheTags.StatementBuilder, id: `TEMPLATE-${templateId}` }) as const;

export const statementBuilderApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    getStatementBuilderTemplates: builder.query<IStatementBuilderTemplatesResponse, StatementBuilderOrgArgs>({
      query: ({ organisationId }) => ({
        url: `statement-builder/templates`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      providesTags: [LIST_TAG],
    }),
    createStatementBuilderTemplate: builder.mutation<IStatementBuilderTemplateResponse, StatementBuilderOrgArgs & IStatementBuilderCreateTemplateRequest>({
      query: ({ organisationId, ...data }) => ({
        url: `statement-builder/templates`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
        data,
      }),
      invalidatesTags: [LIST_TAG],
    }),
    renameStatementBuilderTemplate: builder.mutation<IStatementBuilderTemplateResponse, StatementBuilderQueryArgs & IStatementBuilderRenameTemplateRequest>({
      query: ({ organisationId, templateId, ...data }) => ({
        url: `statement-builder/templates/${templateId}`,
        method: `PATCH`,
        headers: {
          [orgHeader]: organisationId,
        },
        data,
      }),
      invalidatesTags: (_result, _error, { templateId }) => [LIST_TAG, templateTag(templateId)],
    }),
    deleteStatementBuilderTemplate: builder.mutation<{ id: number }, StatementBuilderQueryArgs>({
      query: ({ organisationId, templateId }) => ({
        url: `statement-builder/templates/${templateId}`,
        method: `DELETE`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [LIST_TAG],
    }),
    getStatementBuilderState: builder.query<IStatementBuilderState, StatementBuilderQueryArgs>({
      query: ({ organisationId, templateId }) => ({
        url: `statement-builder/templates/${templateId}`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      providesTags: (_result, _error, { templateId }) => [templateTag(templateId)],
    }),
    getStatementBuilderPreview: builder.query<IStatementBuilderPreviewResponse, StatementBuilderPreviewArgs>({
      query: ({ organisationId, templateId, contactId }) => ({
        url: `statement-builder/templates/${templateId}/preview`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
        params: contactId ? { contactId } : undefined,
      }),
      providesTags: (_result, _error, { templateId }) => [templateTag(templateId)],
    }),
    sendStatementBuilderMessage: builder.mutation<IStatementBuilderMessageResponse, StatementBuilderQueryArgs & IStatementBuilderMessageRequest>({
      query: ({ organisationId, templateId, ...data }) => ({
        url: `statement-builder/templates/${templateId}/message`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
        data,
      }),
      async onQueryStarted({ organisationId, templateId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          // The edit runs on a worker; the builder polls state until the placeholder finishes
          dispatch(statementBuilderApi.util.updateQueryData(`getStatementBuilderState`, { organisationId, templateId }, draft => {
            draft.messages.push(...data.messages);
          }));
        }
        catch {
          // errors are handled by mutation callers
        }
      },
    }),
    revertStatementBuilderToMessage: builder.mutation<IStatementBuilderRevertResponse, StatementBuilderQueryArgs & { messageId: number }>({
      query: ({ organisationId, templateId, messageId }) => ({
        url: `statement-builder/templates/${templateId}/revert/${messageId}`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      // Refetches the preview too, which may be showing a picked contact rather than sample data
      invalidatesTags: (_result, _error, { templateId }) => [templateTag(templateId)],
    }),
    publishStatementBuilder: builder.mutation<IStatementBuilderPublishResponse, StatementBuilderQueryArgs>({
      query: ({ organisationId, templateId }) => ({
        url: `statement-builder/templates/${templateId}/publish`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [LIST_TAG],
      async onQueryStarted({ organisationId, templateId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          dispatch(statementBuilderApi.util.updateQueryData(`getStatementBuilderState`, { organisationId, templateId }, draft => {
            draft.hasPublished = true;
            draft.publishedAt = data.version.publishedAt;
            draft.draftIsPublished = true;
            draft.versions.unshift(data.version);
          }));
        }
        catch {
          // errors are handled by mutation callers
        }
      },
    }),
    updateStatementBuilderEnabled: builder.mutation<IStatementBuilderEnabledResponse, StatementBuilderQueryArgs & IStatementBuilderEnabledRequest>({
      query: ({ organisationId, templateId, ...data }) => ({
        url: `statement-builder/templates/${templateId}/enabled`,
        method: `PATCH`,
        headers: {
          [orgHeader]: organisationId,
        },
        data,
      }),
      // Enabling one template switches the previous default off, so every template's state may change
      invalidatesTags: [CacheTags.StatementBuilder],
    }),
    updateStatementBuilderActivityLookback: builder.mutation<IStatementBuilderActivityLookbackResponse, StatementBuilderQueryArgs & IStatementActivityLookback>({
      query: ({ organisationId, templateId, ...data }) => ({
        url: `statement-builder/templates/${templateId}/activity-lookback`,
        method: `PATCH`,
        headers: {
          [orgHeader]: organisationId,
        },
        data,
      }),
      // Refetches state (draftIsPublished) and the preview's activity section
      invalidatesTags: (_result, _error, { templateId }) => [templateTag(templateId)],
    }),
    updateStatementBuilderOptions: builder.mutation<IStatementBuilderOptionsResponse, StatementBuilderQueryArgs & IStatementTemplateOptions>({
      query: ({ organisationId, templateId, ...data }) => ({
        url: `statement-builder/templates/${templateId}/options`,
        method: `PATCH`,
        headers: {
          [orgHeader]: organisationId,
        },
        data,
      }),
      // Refetches state (draftIsPublished) and the preview's aged analysis and grouping
      invalidatesTags: (_result, _error, { templateId }) => [templateTag(templateId)],
    }),
    resetStatementBuilder: builder.mutation<IStatementBuilderResetResponse, StatementBuilderQueryArgs>({
      query: ({ organisationId, templateId }) => ({
        url: `statement-builder/templates/${templateId}/reset`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: (_result, _error, { templateId }) => [templateTag(templateId)],
    }),
  }),
});

export const {
  useGetStatementBuilderTemplatesQuery,
  useCreateStatementBuilderTemplateMutation,
  useRenameStatementBuilderTemplateMutation,
  useDeleteStatementBuilderTemplateMutation,
  useGetStatementBuilderStateQuery,
  useGetStatementBuilderPreviewQuery,
  useSendStatementBuilderMessageMutation,
  useRevertStatementBuilderToMessageMutation,
  usePublishStatementBuilderMutation,
  useUpdateStatementBuilderEnabledMutation,
  useResetStatementBuilderMutation,
  useUpdateStatementBuilderActivityLookbackMutation,
  useUpdateStatementBuilderOptionsMutation,
} = statementBuilderApi;
